import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Gift, PartyPopper, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductGrid } from '@/components/products/ProductGrid';
import { useSite } from '@/context/SiteContext';

const BANNER_DESKTOP = 'https://customer-assets.emergentagent.com/job_b3658f45-91a7-444d-afc2-1a3b5556caed/artifacts/ru9haz7k_festa-encantada-banner-desktop-final.png';
const BANNER_MOBILE = 'https://customer-assets.emergentagent.com/job_b3658f45-91a7-444d-afc2-1a3b5556caed/artifacts/qyskjx7l_festa-encantada-banner-mobile-sem-borda.png';

const SectionTitle = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="h-6 w-6 text-primary" />}
        <h2 className="section-title">{title}</h2>
      </div>
      {subtitle && <p className="section-subtitle mb-0">{subtitle}</p>}
    </div>
    {action && (
      <Link to={action.href}>
        <Button variant="outline" className="btn-festa-outline text-sm">
          {action.label}
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </Link>
    )}
  </div>
);

const CategoryCard = ({ category, index }) => {
  const colors = [
    'from-primary/20 to-primary/5',
    'from-secondary/20 to-secondary/5',
    'from-accent/20 to-accent/5',
    'from-gold/20 to-gold/5',
  ];
  
  const icons = {
    baloes: '🎈',
    descartaveis: '🍽️',
    decoracao: '✨',
    velas: '🕯️',
    pinhatas: '🪅',
    confettis: '🎊',
    lembrancas: '🎁',
    temas: '🎨',
    kits: '📦',
  };

  return (
    <Link 
      to={`/loja?categoria=${category.id}`}
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors[index % colors.length]} p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
    >
      <div className="text-4xl mb-3">{icons[category.id] || '🎉'}</div>
      <h3 className="font-bold text-foreground text-lg group-hover:text-primary transition-colors">
        {category.name}
      </h3>
      <ArrowRight className="absolute bottom-4 right-4 h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
};

const ThemeButton = ({ theme, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
      isActive 
        ? 'bg-primary text-primary-foreground shadow-md' 
        : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
    }`}
  >
    {theme.name}
  </button>
);

export const HomePage = () => {
  const { siteData, products, getNewProducts, getBestSellers, getKits, getThemeProducts, loading } = useSite();
  const [activeTheme, setActiveTheme] = useState('all');

  const newProducts = getNewProducts().slice(0, 5);
  const bestSellers = getBestSellers().slice(0, 5);
  const kits = getKits().slice(0, 5);
  const themeProducts = getThemeProducts();

  const featuredThemes = siteData?.featuredThemes || [];
  
  const filteredThemeProducts = activeTheme === 'all' 
    ? themeProducts.slice(0, 8)
    : themeProducts.filter(p => 
        p.tags?.includes(activeTheme) || 
        p.subcategory === activeTheme
      ).slice(0, 8);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative">
        <picture>
          <source media="(min-width: 768px)" srcSet={BANNER_DESKTOP} />
          <img 
            src={BANNER_MOBILE} 
            alt="Festa Encantada - Magia em cada detalhe da tua festa"
            className="w-full h-auto object-cover"
          />
        </picture>
        
        {/* CTA Overlay - positioned higher on mobile */}
        <div className="absolute bottom-4 sm:bottom-8 left-0 right-0 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 px-4">
          <Link to="/loja?filter=novidades">
            <Button className="btn-festa shadow-lg text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3">
              <Sparkles className="h-4 w-4 mr-2" />
              Ver Novidades
            </Button>
          </Link>
          <Link to="/loja?categoria=temas">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3">
              <PartyPopper className="h-4 w-4 mr-2" />
              Ver Temas
            </Button>
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      <section className="container mx-auto px-4 py-12">
        <SectionTitle 
          icon={Sparkles}
          title="Categorias" 
          subtitle="Encontra tudo para a tua festa perfeita"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {siteData?.categories?.slice(0, 10).map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>
      </section>

      {/* New Products Section */}
      <section className="bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <SectionTitle 
            icon={Star}
            title="Novidades" 
            subtitle="Os produtos mais recentes da nossa loja"
            action={{ label: 'Ver Todas', href: '/loja?filter=novidades' }}
          />
          <ProductGrid products={newProducts} columns={5} />
        </div>
      </section>

      {/* Featured Themes Section */}
      <section className="container mx-auto px-4 py-12">
        <SectionTitle 
          icon={PartyPopper}
          title="Temas em Destaque" 
          subtitle="Decoração completa para cada tema de festa"
          action={{ label: 'Ver Todos', href: '/loja?categoria=temas' }}
        />
        
        {/* Theme Filters */}
        <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2">
          <ThemeButton 
            theme={{ id: 'all', name: 'Todos' }} 
            isActive={activeTheme === 'all'} 
            onClick={() => setActiveTheme('all')} 
          />
          {featuredThemes.slice(0, 6).map((theme) => (
            <ThemeButton 
              key={theme.id} 
              theme={theme} 
              isActive={activeTheme === theme.id} 
              onClick={() => setActiveTheme(theme.id)} 
            />
          ))}
        </div>
        
        <ProductGrid products={filteredThemeProducts.length > 0 ? filteredThemeProducts : themeProducts.slice(0, 8)} columns={4} />
      </section>

      {/* Best Sellers Section */}
      <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 py-12">
        <div className="container mx-auto px-4">
          <SectionTitle 
            icon={TrendingUp}
            title="Mais Vendidos" 
            subtitle="Os favoritos dos nossos clientes"
            action={{ label: 'Ver Todos', href: '/loja?filter=mais-vendidos' }}
          />
          <ProductGrid products={bestSellers} columns={5} />
        </div>
      </section>

      {/* Kits Section */}
      <section className="container mx-auto px-4 py-12">
        <SectionTitle 
          icon={Gift}
          title="Kits Prontos" 
          subtitle="Tudo o que precisas num só pack - prático e económico!"
          action={{ label: 'Ver Todos', href: '/loja?categoria=kits' }}
        />
        <ProductGrid products={kits} columns={5} />
      </section>

      {/* Shipping Info Banner */}
      <section className="bg-secondary py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-secondary-foreground/20 rounded-full flex items-center justify-center">
                <Gift className="h-6 w-6 text-secondary-foreground" />
              </div>
              <div className="text-left">
                <p className="font-bold text-secondary-foreground">Portes Grátis</p>
                <p className="text-secondary-foreground/80 text-sm">Em compras a partir de 50,00 €</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-secondary-foreground/20" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-secondary-foreground/20 rounded-full flex items-center justify-center">
                <PartyPopper className="h-6 w-6 text-secondary-foreground" />
              </div>
              <div className="text-left">
                <p className="font-bold text-secondary-foreground">Levantamento em 1h</p>
                <p className="text-secondary-foreground/80 text-sm">Na nossa loja em Cascais</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
